<template>
    <div class="container">
        <el-card shadow="never" class="card-border">
            <template #header>
                <div class="card-header">
                    <span>商户开通数电发票</span>
                </div>
            </template>

            <div class="card-content">
                <el-form ref="formRef" :model="formData" :rules="rules" label-width="140px"
                    style="max-width: 600px; margin: 0 auto;" v-loading="pageLoading">
                    <el-form-item label="开通通道:" prop="channel">
                        <el-radio-group v-model="formData.channel" @change="handleChannelChange">
                            <el-radio :label="1">微信数电发票</el-radio>
                            <el-radio :label="2">支付宝数电发票</el-radio>
                        </el-radio-group>
                    </el-form-item>

                    <el-form-item label="企业税号:" prop="tax_no">
                        <el-input v-model="formData.tax_no" placeholder="请与营业执照上的统一社会信用代码保持一致" clearable />
                    </el-form-item>

                    <el-form-item label="通道子商户号:" prop="sub_mchid" v-if="formData.channel === 1">
                        <el-input v-model="formData.sub_mchid" placeholder="微信通道必传" clearable />
                    </el-form-item>

                    <el-form-item label="开通产品类型:" prop="product_id">
                        <el-select v-model="formData.product_id" placeholder="请选择产品类型" style="width: 100%;">
                            <el-option v-for="item in productOptions" :key="item.value" :label="item.label"
                                :value="item.value" />
                        </el-select>
                    </el-form-item>

                    <el-form-item>
                        <el-button type="primary" @click="submitForm" :loading="submitLoading">
                            保存并发起开通绑定
                        </el-button>
                        <el-button @click="resetForm">重置表单</el-button>
                    </el-form-item>
                </el-form>
            </div>
        </el-card>

        <el-dialog v-model="resultVisible" :title="resultData?.isHistory ? '未完成的开通申请' : '开通申请结果'" width="500px" center>
            <div v-if="resultData" class="result-container">
                <el-result :icon="resultData.isHistory ? 'warning' : 'success'"
                    :title="resultData.isHistory ? '存在进行中的开通申请' : '申请已提交'"
                    :sub-title="resultData.isHistory ? '请按指引完成授权操作' : '请按指引完成授权操作'">
                    <template #extra>
                        <div v-if="formData.channel === 1" class="wechat-result">
                            <p class="tips">
                                {{ resultData.isHistory ? '该商户正在开通中，可直接扫描下方上一次的邀请码继续：' : '请使用微信扫描下方二维码跳转至小程序开通：' }}
                            </p>
                            <div class="qrcode-placeholder">
                                <qrcode-vue v-if="resultData.inviteUrl" :value="resultData.inviteUrl" :size="160"
                                    level="H" />
                                <p class="qrcode-tips">微信扫一扫开通</p>
                            </div>
                        </div>

                        <div v-if="formData.channel === 2" class="alipay-result">
                            <p class="tips">
                                {{ resultData.isHistory ? '检测到该商户正在开通中，请使用企业支付宝扫描下方二维码继续：' : '请使用企业支付宝扫描下方二维码授权开通：' }}
                            </p>
                            <div class="qrcode-placeholder">
                                <qrcode-vue v-if="resultData.inviteUrl" :value="resultData.inviteUrl" :size="160"
                                    level="H" />
                                <p class="qrcode-tips">支付宝扫一扫开通</p>
                            </div>
                            <div class="extra-info">
                                <p v-if="resultData.businessId">业务申请ID: {{ resultData.businessId }}</p>
                                <p v-if="resultData.applyId">申请流水ID: {{ resultData.applyId }}</p>
                                <el-link type="primary" :underline="false" @click="copyUrl(resultData.inviteUrl)"
                                    style="margin-top: 5px;">
                                    无法扫码？点击复制授权链接
                                </el-link>
                            </div>
                        </div>
                    </template>
                </el-result>
            </div>
            <template #footer>
                <div class="dialog-footer">
                    <el-button type="primary" @click="resultVisible = false">确认</el-button>
                </div>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import QrcodeVue from 'qrcode.vue'

// 获取全局 API 实例
const { appContext } = getCurrentInstance()
const $api = appContext.config.globalProperties.$api

const formRef = ref(null)
const pageLoading = ref(false)
const submitLoading = ref(false)
// const statusLoading = ref(false)
const resultVisible = ref(false)
const resultData = ref(null)

// 暂存完整的原始配置数据，用于切换通道时动态提取对应的历史数据
const rawConfig = ref(null)

// 1. 表单数据模型
const formData = reactive({
    channel: 1,
    sub_mchid: '',
    tax_no: '',
    product_id: '',
})

// 用于在初始化时静默暂存历史邀请数据，提交失败时兜底显示（动态计算）
const historyData = computed(() => {
    if (!rawConfig.value) return { inviteUrl: '', businessId: '', applyId: '' }

    // 根据当前选中的通道，动态获取对应通道的历史数据，实现切换通道后兜底数据也同步切换
    if (formData.channel === 1) {
        return {
            inviteUrl: rawConfig.value.wx_last_invite_url || '',
            businessId: rawConfig.value.wx_last_business_id || '',
            applyId: rawConfig.value.wx_last_apply_id || ''
        }
    } else {
        return {
            inviteUrl: rawConfig.value.ali_last_invite_url || '',
            businessId: rawConfig.value.ali_last_business_id || '',
            applyId: rawConfig.value.ali_last_apply_id || ''
        }
    }
})

// 2. 产品类型动态选项
const productOptions = computed(() => {
    if (formData.channel === 1) {
        return [
            { label: '基础开票能力 (餐饮、酒店等)', value: 'BASE_ABILITY' },
            { label: '成品油能力 (石油石化等)', value: 'REFINED_OIL_ABILITY' },
            { label: '不动产租赁能力 (停车场等)', value: 'REAL_ESTATE_ABILITY' }
        ]
    } else {
        return [
            { label: '成品油发票', value: '2025060044528616' }
        ]
    }
})

// 3. 表单校验规则
const rules = reactive({
    channel: [{ required: true, message: '请选择通道', trigger: 'change' }],
    tax_no: [{ required: true, message: '请输入企业税号', trigger: 'blur' }],
    sub_mchid: [
        {
            validator: (rule, value, cb) => {
                if (formData.channel === 1 && !value) return cb(new Error('微信通道必须填写子商户号'))
                cb()
            },
            trigger: 'blur'
        }
    ],
    product_id: [{ required: true, message: '请选择产品类型', trigger: 'change' }]
})

// --- 逻辑方法 ---

// 初始化：获取当前商户已有的发票配置
const initConfig = async () => {
    pageLoading.value = true
    try {
        const res = await $api.invoice.getConfig()
        // 根据新结构更新：res.data.config 直接是数据对象
        if (res.status === 200 && res.data && res.data.config) {
            const savedConfig = res.data.config
            // 缓存原始配置，供计算属性 historyData 使用
            rawConfig.value = savedConfig

            // 回填表单数据
            if (savedConfig.channel) formData.channel = Number(savedConfig.channel)
            formData.tax_no = savedConfig.tax_no || ''
            formData.sub_mchid = savedConfig.sub_mchid || ''
            formData.product_id = savedConfig.product_id || ''
        }
    } catch (error) {
        console.error('加载配置失败', error)
    } finally {
        pageLoading.value = false
    }
}

// 切换通道时清理校验状态
const handleChannelChange = () => {
    formData.product_id = ''
    // 如果切换到支付宝，清空子商户号；如果切换到微信，尝试自动回填新接口返回的 sub_mchid 
    if (formData.channel === 1) {
        formData.sub_mchid = rawConfig.value?.sub_mchid || ''
    } else {
        formData.sub_mchid = ''
    }
    if (formRef.value) formRef.value.clearValidate('sub_mchid')
}

// 提交：发起开通邀请
const submitForm = async () => {
    if (!formRef.value) return
    await formRef.value.validate(async (valid) => {
        if (!valid) return ElMessage.error('请检查必填项')

        submitLoading.value = true
        try {
            // 1. 先行查询当前通道的开通状态
            const queryParams = { channel: formData.channel }
            // 微信通道查询状态时必须传入子商户号
            if (formData.channel === 1) {
                queryParams.sub_mchid = formData.sub_mchid
            }

            const statusRes = await $api.invoice.queryOpenStatus(queryParams)
            let isOpened = false

            if (statusRes.status === 200 && statusRes.data) {
                // 兼容多层级返回结构
                const responseInfo = statusRes.data.response || statusRes.data.data?.response || statusRes.data

                if (formData.channel === 1) {
                    // 微信数电发票成功条件：digitalTaxMode.taxStatus == 'ENABLED' 且 abilityInfo 数组中存在 status == 'AUTHORIZED' 的项
                    const taxStatus = responseInfo.digitalTaxMode?.taxStatus
                    const abilityList = responseInfo.digitalTaxMode?.abilityInfo || []
                    const hasAuthorizedAbility = abilityList.some(item => item.status === 'AUTHORIZED')

                    if (taxStatus === 'ENABLED' && hasAuthorizedAbility) {
                        isOpened = true
                    }
                } else if (formData.channel === 2) {
                    // 支付宝数电发票成功条件：status == 'AUTH_SUCCESS'
                    if (responseInfo.status === 'AUTH_SUCCESS') {
                        isOpened = true
                    }
                }
            }

            // 2. 如果已经是开通成功状态，提示并直接拦截退出
            if (isOpened) {
                ElMessage.success('该通道数电发票已开通成功，无需重复开通')
                submitLoading.value = false
                return
            }

            // 3. 如果不是开通成功状态，继续执行保存并发起开通流程
            const res = await $api.invoice.openInvitation(formData)
            if (res.status === 200) {
                ElMessage.success('申请提交成功')
                const responseInfo = res.data.response || {}
                resultData.value = {
                    inviteUrl: responseInfo.inviteUrl,
                    businessId: responseInfo.businessId,
                    applyId: responseInfo.applyId,
                    isHistory: false
                }
                resultVisible.value = true
            } else {
                const errMsg = res.error?.message || ''
                // 如果不是成功状态，统一兜底显示二维码（包含“正在申请开通中”或其他非成功响应）
                if (historyData.value.inviteUrl) {
                    ElMessage.warning(errMsg || '该商户正在申请开通中')
                    resultData.value = {
                        inviteUrl: historyData.value.inviteUrl,
                        businessId: historyData.value.businessId,
                        applyId: historyData.value.applyId,
                        isHistory: true
                    }
                    resultVisible.value = true
                } else {
                    if (errMsg.includes('http')) {
                        const htmlMsg = errMsg.replace(
                            /(https?:\/\/[^\s]+)/g,
                            '<a href="$1" target="_blank" style="color: #409EFF; text-decoration: none;">$1</a>'
                        )
                        ElMessageBox.alert(
                            `<div style="line-height: 1.5; word-break: break-all;">${htmlMsg}</div>`,
                            '开通申请失败',
                            {
                                dangerouslyUseHTMLString: true,
                                type: 'error',
                                confirmButtonText: '知道了'
                            }
                        )
                    } else {
                        ElMessage.error(errMsg || '开通申请失败')
                    }
                }
            }
        } catch (error) {
            console.error(error)
            ElMessage.error('网络异常或系统错误')
        } finally {
            submitLoading.value = false
        }
    })
}

// 查询开通状态（保留原逻辑方法以维持完整性，外部触发已取消）
// const handleQueryStatus = async () => {
//     statusLoading.value = true
//     try {
//         const queryParams = { channel: formData.channel }
//         if (formData.channel === 1) {
//             queryParams.sub_mchid = formData.sub_mchid
//         }
//         const res = await $api.invoice.queryOpenStatus(queryParams)
//         if (res.status === 200) {
//             const statusDesc = res.data.status_desc || '查询成功，请查看返回报文'
//             ElMessageBox.alert(
//                 `<div style="word-break:break-all">
//           <p><strong>当前状态：</strong>${statusDesc}</p>
//           <hr style="margin:10px 0; border:0; border-top:1px solid #eee" />
//           <p style="font-size:12px; color:#999">原始信息：${JSON.stringify(res.data)}</p>
//         </div>`,
//                 '状态查询...结果',
//                 { dangerouslyUseHTMLString: true }
//             )
//         } else {
//             ElMessage.error(res.error.message || '查询失败')
//         }
//     } catch (error) {
//         console.error(error)
//     } finally {
//         statusLoading.value = false
//     }
// }

// 复制链接功能
const copyUrl = (url) => {
    if (!url) return
    navigator.clipboard.writeText(url).then(() => {
        ElMessage.success('链接已复制到剪贴板')
    }).catch(() => {
        // 兼容处理
        const input = document.createElement('input')
        input.value = url
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
        ElMessage.success('链接已复制到剪贴板')
    })
}

// 重置
const resetForm = () => {
    formRef.value?.resetFields()
}

onMounted(() => {
    initConfig()
})
</script>

<style lang="less" scoped>
/* 保持原有样式不作改动 */
.container {
    padding: 20px;

    .card-border {
        border-top: 5px solid #e7e7e7;

        .card-header {
            font-size: 16px;
            font-weight: bold;
        }

        .card-content {
            margin-top: 30px;
        }
    }
}

.result-container {
    .tips {
        font-size: 14px;
        color: #606266;
        margin-bottom: 15px;
        text-align: center;
    }

    .qrcode-placeholder {
        width: 220px;
        height: 220px;
        background-color: #f9f9f9;
        border: 1px dashed #dcdfe6;
        margin: 0 auto;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 15px;
        text-align: center;

        .qrcode-tips {
            font-size: 13px;
            color: #666;
            margin-top: 12px;
            font-weight: bold;
        }
    }

    .extra-info {
        margin-top: 15px;
        font-size: 13px;
        color: #666;
        background: #f5f7fa;
        padding: 10px;
        border-radius: 4px;
        text-align: center;

        p {
            margin: 5px 0;
        }
    }
}
</style>